import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { colors, sans, tokens } from "../theme.ts";
import { GlassFloat } from "./glass.tsx";

export type Tab = "board" | "calendar" | "home" | "week" | "status";

type IconProps = { size?: number };
function Icon({ size = 20, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
  );
}

// Board, Calendar and Home are the glyphs V1's nav uses for the same things.
const IconBoard = ({ size }: IconProps) => <Icon size={size}><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 10h16M10 10v10" /></Icon>;
const IconCalendar = ({ size }: IconProps) => <Icon size={size}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16M8 3v4M16 3v4" /></Icon>;
const IconHome = ({ size }: IconProps) => <Icon size={size}><path d="M4 12l8-8 8 8M6 10v10h12V10" /></Icon>;
const IconWeek = ({ size }: IconProps) => <Icon size={size}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16M8 13.5h8M8 16.5h5" /></Icon>;
const IconStatus = ({ size }: IconProps) => <Icon size={size}><path d="M3 12h4l3-7 4 14 3-7h4" /></Icon>;

/** Home sits in the centre of the bar (scope, decided 01.10). */
const TABS: { key: Tab; label: string; Icon: (p: IconProps) => React.ReactElement }[] = [
  { key: "board", label: "Board", Icon: IconBoard },
  { key: "calendar", label: "Calendar", Icon: IconCalendar },
  { key: "home", label: "Home", Icon: IconHome },
  { key: "week", label: "Week", Icon: IconWeek },
  { key: "status", label: "Status", Icon: IconStatus },
];

/**
 * The one floating glass pill. No badges, no counts, no dots; the active tab
 * is the soft accent disc. Labels are measured once per size change and, if
 * one would clip at a large system font size, all shrink together.
 */
export function Nav({ active, onNavigate }: { active: Tab; onNavigate: (t: Tab) => void }) {
  const pill = useRef<HTMLDivElement>(null);
  const [labelScale, setLabelScale] = useState(1);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const remeasure = () => { setLabelScale(1); setTick((t) => t + 1); };
    window.addEventListener("resize", remeasure);
    document.fonts?.ready.then(remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, []);
  useLayoutEffect(() => {
    if (labelScale !== 1 || !pill.current) return;
    let ratio = 1;
    pill.current.querySelectorAll<HTMLElement>("[data-label]").forEach((el) => {
      if (el.scrollWidth > el.clientWidth) ratio = Math.min(ratio, el.clientWidth / el.scrollWidth);
    });
    if (ratio < 1) setLabelScale(Math.max(0.6, ratio * 0.97));
  }, [tick, labelScale]);

  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, maxWidth: 560, margin: "0 auto",
      padding: `0 max(${tokens.space.dockInset}px, env(safe-area-inset-right)) calc(var(--nav-gap) + env(safe-area-inset-bottom)) max(${tokens.space.dockInset}px, env(safe-area-inset-left))`,
      zIndex: 30, pointerEvents: "none",
    }}>
      <GlassFloat style={{ padding: "6px", pointerEvents: "auto", minWidth: 0 }}>
        <nav ref={pill} style={{ display: "flex", alignItems: "stretch" }}>
          {TABS.map(({ key, label, Icon: I }) => {
            const on = key === active;
            return (
              <button
                key={key} onClick={() => onNavigate(key)} aria-label={label} aria-current={on ? "page" : undefined}
                style={{
                  flex: "1 1 0", minWidth: 0, minHeight: 56,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                  padding: "6px 0", borderRadius: 999, border: "none",
                  background: on ? colors.accentSoft : "transparent", color: on ? colors.ink : colors.ink2,
                  cursor: "pointer", fontFamily: sans, fontSize: tokens.type.navLabel.size,
                  letterSpacing: tokens.type.navLabel.letterSpacing,
                }}
              >
                <I size={tokens.size.navIcon} />
                <span data-label style={{
                  maxWidth: "100%", overflow: "hidden", textOverflow: "clip", whiteSpace: "nowrap",
                  fontSize: labelScale === 1 ? undefined : `${tokens.type.navLabel.size * labelScale}px`,
                }}>{label}</span>
              </button>
            );
          })}
        </nav>
      </GlassFloat>
    </div>
  );
}
